import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { snapOverlayToActivator } from 'src/helpers/dndModifiers';
import { pageBlockCollisionDetection } from 'src/helpers/pageBlockCollisionDetection';
import {
  BODY_ROOT_DROP_ID,
  documentHasSponsorsBlock,
  getBodyBlocks,
  getHeroBlocks,
  HERO_ROOT_DROP_ID,
  IPageDocument,
  layoutColSortableId,
} from 'src/helpers/pageDocumentUtils';
import { documentHasSignupFlowBlock } from 'src/helpers/signupPageLayoutUtils';
import { IPageBlock, PageBlockType } from 'src/interfaces/PageBlock';
import { ITrackMap } from 'src/interfaces/TrackMap';
import { ALL_PAGE_BLOCK_TYPES } from 'src/helpers/pageBlockFactory';
import AddBlockMenu from 'src/pages/Dashboard/System/Pages/components/AddBlockMenu';
import BlockDropZone from 'src/pages/Dashboard/System/Pages/components/BlockDropZone';
import BlockListInsertDivider from 'src/pages/Dashboard/System/Pages/components/BlockListInsertDivider';
import { LanguageFlagButtons } from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';
import PageBlocksSectionHeader from 'src/pages/Dashboard/System/Pages/components/PageBlocksSectionHeader';
import SortableBlockCard from 'src/pages/Dashboard/System/Pages/components/SortableBlockCard';

function collectSortableIds(document: IPageDocument): string[] {
  const ids: string[] = [HERO_ROOT_DROP_ID, BODY_ROOT_DROP_ID];
  for (const block of document.blocks) {
    ids.push(block.id);
    if (block.type === 'columns') {
      for (let i = 0; i < block.columns.length; i++) {
        ids.push(layoutColSortableId(block.id, i));
        for (const nested of block.columns[i]!.blocks) {
          ids.push(nested.id);
        }
      }
    }
  }
  return ids;
}

interface PageDocumentEditorProps {
  document: IPageDocument;
  onDocumentChange: (doc: IPageDocument) => void;
  contentKey: string;
  pageContentId?: string;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  trackMaps: ITrackMap[];
  showHeroZone?: boolean;
  showBodyZone?: boolean;
  heroSectionTitleKey?: string;
  bodySectionTitleKey?: string;
  isSignupShell?: boolean;
  allowedBlockTypes?: PageBlockType[];
  onMoveBlock: (id: string, direction: 'up' | 'down') => void;
  onMoveByDrag: (activeId: string, overId: string) => void;
  onUpdate: (id: string, patch: Partial<IPageBlock>) => void;
  onRemove: (id: string) => void;
  onAddAt: (
    zone: 'hero' | 'body',
    type: PageBlockType,
    columnCount?: 2 | 3 | 4,
    insertIndex?: number,
  ) => void;
}

export default function PageDocumentEditor({
  document,
  onDocumentChange,
  contentKey,
  pageContentId,
  selectedEditorLang,
  setSelectedEditorLang,
  trackMaps,
  showHeroZone = false,
  showBodyZone = true,
  heroSectionTitleKey = 'system.pages.hero.blocksEditor',
  bodySectionTitleKey = 'system.pages.blocksEditor',
  isSignupShell = false,
  allowedBlockTypes,
  onMoveBlock,
  onMoveByDrag,
  onUpdate,
  onRemove,
  onAddAt,
}: PageDocumentEditorProps) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const heroBlocks = getHeroBlocks(document);
  const bodyBlocks = getBodyBlocks(document);
  const hasSponsors = documentHasSponsorsBlock(document);
  const hasSignupFlow = documentHasSignupFlowBlock(document);
  const sortableIds = useMemo(() => collectSortableIds(document), [document]);

  const addDisabledTypes: Partial<Record<PageBlockType, boolean>> = {
    sponsors: hasSponsors,
    ...(isSignupShell && hasSignupFlow ? { signupFlow: true } : {}),
  };
  if (allowedBlockTypes) {
    for (const type of ALL_PAGE_BLOCK_TYPES) {
      if (!allowedBlockTypes.includes(type)) {
        addDisabledTypes[type] = true;
      }
    }
  }
  const addDisabledTitles: Partial<Record<PageBlockType, string>> = {
    sponsors: hasSponsors ? t('system.pages.sponsorsBlockAlreadyAdded') : undefined,
    signupFlow: hasSignupFlow ? t('system.pages.signupFlowBlockAlreadyAdded') : undefined,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeBlock = useMemo(() => {
    if (!activeId) return undefined;
    const find = (blocks: IPageBlock[]): IPageBlock | undefined => {
      for (const b of blocks) {
        if (b.id === activeId) return b;
        if (b.type === 'columns') {
          for (const col of b.columns) {
            const nested = col.blocks.find(n => n.id === activeId);
            if (nested) return nested;
          }
        }
      }
      return undefined;
    };
    return find(document.blocks);
  }, [activeId, document.blocks]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    onMoveByDrag(String(active.id), String(over.id));
  };

  const renderZone = (
    zone: 'hero' | 'body',
    blocks: IPageBlock[],
    titleKey: string,
    droppableId: string,
    canAddColumns: boolean,
  ) => (
    <Box sx={{ mb: 3 }}>
      <PageBlocksSectionHeader
        title={t(titleKey)}
        actions={
          <>
            {zone === 'body' ? (
              <LanguageFlagButtons
                value={selectedEditorLang}
                onChange={setSelectedEditorLang}
              />
            ) : null}
            <AddBlockMenu
              canAddColumns={canAddColumns}
              disabledTypes={addDisabledTypes}
              disabledTitles={addDisabledTitles}
              onAdd={(type, columnCount) => onAddAt(zone, type, columnCount)}
            />
          </>
        }
      />
      <BlockDropZone
        droppableId={droppableId}
        emptyHint={undefined}
      >
        {blocks.length === 0 ? (
          <BlockListInsertDivider
            canAddColumns={canAddColumns}
            disabledTypes={addDisabledTypes}
            disabledTitles={addDisabledTitles}
            onAdd={(type, columnCount) => onAddAt(zone, type, columnCount, 0)}
          />
        ) : (
          blocks.map((block, index) => (
            <Fragment key={block.id}>
              <BlockListInsertDivider
                canAddColumns={canAddColumns}
                disabledTypes={addDisabledTypes}
                disabledTitles={addDisabledTitles}
                onAdd={(type, columnCount) => onAddAt(zone, type, columnCount, index)}
              />
              <SortableBlockCard
                block={block}
                index={index}
                containerBlockCount={blocks.length}
                document={document}
                contentKey={contentKey}
                pageContentId={pageContentId}
                selectedEditorLang={selectedEditorLang}
                setSelectedEditorLang={setSelectedEditorLang}
                trackMaps={trackMaps}
                hasSponsorsBlock={hasSponsors}
                isSignupShell={isSignupShell}
                onUpdate={onUpdate}
                onRemove={onRemove}
                onMove={onMoveBlock}
                onColumnsChange={(columnsBlockId, patch) => onUpdate(columnsBlockId, patch)}
                onDocumentChange={onDocumentChange}
              />
            </Fragment>
          ))
        )}
        {blocks.length > 0 ? (
          <BlockListInsertDivider
            canAddColumns={canAddColumns}
            disabledTypes={addDisabledTypes}
            disabledTitles={addDisabledTitles}
            onAdd={(type, columnCount) => onAddAt(zone, type, columnCount, blocks.length)}
          />
        ) : null}
      </BlockDropZone>
    </Box>
  );

  if (!contentKey.trim()) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('system.pages.contentEditorNeedsKey')}
      </Typography>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pageBlockCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {showHeroZone ? renderZone('hero', heroBlocks, heroSectionTitleKey, HERO_ROOT_DROP_ID, true) : null}
        {showBodyZone
          ? renderZone('body', bodyBlocks, bodySectionTitleKey, BODY_ROOT_DROP_ID, true)
          : null}
      </SortableContext>
      <DragOverlay modifiers={[snapOverlayToActivator]} dropAnimation={null}>
        {activeBlock ? (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, boxShadow: 4, cursor: 'grabbing', bgcolor: 'background.paper' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DragIndicatorIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                {t(`system.pages.blockTypes.${activeBlock.type}`)}
              </Typography>
            </Box>
          </Paper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
